import React, { ChangeEvent, useEffect, useMemo, useState } from "react";
import Card from "react-bootstrap/Card";
import Form from "react-bootstrap/Form";
import UnitTypeComponent from "./UnitType";
import { UnitGroup } from "../../features/rapidstor/entities/UnitType";
import type UnitTypeEntity from "../../features/rapidstor/entities/UnitType";
import type { StandardSizeMatch } from "../../features/rapidstor/entities/StandardSize";
import type DescriptorEntity from "../../features/rapidstor/entities/Descriptor";
import classes from "./Descriptor.module.scss";
import SmallDetail from "../SmallDetail";
import { Link } from "react-router-dom";
import { ReactComponent as IconRightChevron } from "../../assets/icon-right-chevron.svg";
import Location from "../../features/rapidstor/entities/Location";

interface Props extends React.HTMLAttributes<HTMLElement> {
  match: StandardSizeMatch;
  recommended: boolean;
  slideIndex: number;
  onBookUnitClicked: (unitType: UnitTypeEntity) => void;
  onEmailQuoteClicked: () => void;
  onUnitTypeSelected: (
    descriptor: DescriptorEntity,
    unitType: UnitTypeEntity,
    slideIndex: number
  ) => void;
  location: Location;
}

const groupLabels: Record<UnitGroup, string> = {
  upper: "Upper Level",
  ground: "Ground Floor",
  driveup: "Drive Up",
};

function StandardSizeSlide({
  onBookUnitClicked,
  onEmailQuoteClicked,
  onUnitTypeSelected,
  slideIndex,
  location,
  match,
  recommended,
}: Props) {
  const { unitGroups, definition, descriptor, descriptorByUnitTypeId } = match;

  const validGroups = useMemo(
    () =>
      Object.entries(unitGroups)
        .filter(([, type]) => type)
        .map(([group]) => group as UnitGroup)
        .sort(),
    [unitGroups]
  );

  const bestAvailableGroup = useMemo(
    () =>
      Object.entries(unitGroups)
        .filter(([, type]) => type)
        .sort(([, a], [, b]) => {
          if (a === null || b === null) return -1;
          if (!!a.totalAvailable === !!b.totalAvailable) {
            return a.stdRate - b.stdRate;
          }
          return a.totalAvailable ? -1 : 1;
        })
        .map(([group]) => group as UnitGroup)[0],
    [unitGroups]
  );

  const [group, setGroup] = useState<UnitGroup>(
    bestAvailableGroup ?? validGroups[0] ?? "ground"
  );
  const unitType = useMemo(
    () => unitGroups[group] as UnitTypeEntity | null,
    [group, unitGroups]
  );

  const activeDescriptor = useMemo(() => {
    if (!unitType) return descriptor;
    return descriptorByUnitTypeId.get(unitType._id) ?? descriptor;
  }, [unitType, descriptor, descriptorByUnitTypeId]);

  const onChangeEvent = (event: ChangeEvent<HTMLInputElement>) => {
    setGroup(event.currentTarget.value as UnitGroup);
  };

  useEffect(() => {
    if (activeDescriptor && unitType) {
      onUnitTypeSelected(activeDescriptor, unitType, slideIndex);
    }
  }, [activeDescriptor, unitType, slideIndex, onUnitTypeSelected]);

  const hasUnits = validGroups.length > 0 && unitType !== null;

  return (
    <div className={classes.descriptor}>
      <Card
        className={classes.card}
        style={{ backgroundColor: "rgba(0,0,0,0.05)", border: "0", height: "100%" }}
      >
        <Card.Body className={classes.card_body}>
          {hasUnits && activeDescriptor ? (
            <div className="row">
              <div className="col-sm-12 col-md-6">
                <UnitTypeComponent
                  onBookUnitClicked={() => unitType && onBookUnitClicked(unitType)}
                  onEmailQuoteClicked={onEmailQuoteClicked}
                  descriptor={activeDescriptor}
                  unitType={unitType}
                  recommended={recommended}
                  sizeLabel={definition.label}
                />
              </div>
              <div className="mt-2 col-sm-12 col-md-6">
                <SmallDetail bold className="mb-1">
                  Accessibility
                </SmallDetail>
                <div
                  className={`${classes.groupSelection} d-flex column-gap-2 flex-wrap square-check`}
                >
                  {validGroups.map((_group) => (
                    <button
                      key={_group}
                      onMouseDown={() => setGroup(_group)}
                      style={{
                        border: "none",
                        backgroundColor: "transparent",
                        color: "#000",
                      }}
                    >
                      <Form.Check
                        type="checkbox"
                        name={`position-${definition.label}`}
                        id={`position-${definition.label}-${_group}`}
                        label={groupLabels[_group]}
                        key={_group}
                        value={_group}
                        checked={group === _group}
                        onChange={onChangeEvent}
                      />
                    </button>
                  ))}
                </div>
                <div className={classes.buttons}>
                  <div className="d-flex gap-3 my-4">
                    {unitType.totalAvailable > 0 ? (
                      <button
                        type="button"
                        style={{ fontSize: 14 }}
                        onClick={() => unitType && onBookUnitClicked(unitType)}
                        className="btn-md w-100 btn btn-primary"
                      >
                        Book Now
                      </button>
                    ) : (
                      <Link className="w-100" to={`/contact/${location.slug}`}>
                        <button
                          type="button"
                          style={{ fontSize: 14 }}
                          className="btn-md w-100 btn btn-primary"
                        >
                          Call For Availability
                        </button>
                      </Link>
                    )}
                    <button
                      type="button"
                      style={{ fontSize: 14 }}
                      onClick={onEmailQuoteClicked}
                      className="btn-md w-100 btn bg-white btn-outline-primary"
                    >
                      Email Quote
                    </button>
                  </div>
                </div>
                <div className={classes.descriptor_description_desktop}>
                  <SmallDetail bold>{activeDescriptor.description}</SmallDetail>
                  {unitType.deal && unitType.deal.descriptions.length > 0 && (
                    <ul className={`${classes.dotPoints} mt-2`}>
                      {unitType.deal.descriptions.map((desc, index) => (
                        <li key={index}>{desc}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <UnavailablePlaceholder
              label={definition.label}
              locationSlug={location.slug}
            />
          )}
        </Card.Body>
        <IconRightChevron className={classes.arrow} />
      </Card>

      {hasUnits && activeDescriptor ? (
        <div className={classes.descriptor_description_mobile}>
          <SmallDetail bold>{activeDescriptor.description}</SmallDetail>
          {unitType.deal && unitType.deal.descriptions.length > 0 && (
            <ul className={`${classes.dotPoints} mt-2`}>
              {unitType.deal.descriptions.map((desc, index) => (
                <li key={index}>{desc}</li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}

function UnavailablePlaceholder({
  label,
  locationSlug,
}: {
  label: string;
  locationSlug: string;
}) {
  return (
    <div className="text-center py-4">
      <div style={{ fontSize: 13, color: "rgba(0,0,0,0.5)" }}>Standard Size</div>
      <div className="fw-semibold" style={{ fontSize: 28, lineHeight: "34px" }}>
        {label}
      </div>
      <div
        className="bg-white my-3 mx-auto d-flex align-items-center justify-content-center"
        style={{
          borderRadius: 4,
          height: 120,
          maxWidth: 300,
          color: "rgba(0,0,0,0.35)",
          fontSize: 14,
        }}
      >
        No units currently in stock
      </div>
      <div className="mt-3">
        <Link to={`/contact/${locationSlug}`}>
          <button
            type="button"
            style={{ fontSize: 14 }}
            className="btn-md btn btn-primary"
          >
            Call For Availability
          </button>
        </Link>
      </div>
    </div>
  );
}

export default StandardSizeSlide;
